import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import React, { useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import CartCheckbox from '../CartCheckbox/CartCheckbox';
import CartDiscountMessage from '../CartDiscountMessage/CartDiscountMessage';

import { BtnArrow } from '../Icons';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

import { StyledForm } from './CartDiscountStyles';

var cartDiscountReducer = function cartDiscountReducer(state, action) {
  switch (action.type) {
    case 'INPUT_CHANGE':
      return Object.assign({}, state, {
        input: action.input,
        active: true,
        autoDiscountPromo: false,
        submitMessageType: ''
      });
    case 'TOGGLE_ACTIVE':
      return Object.assign({}, state, {
        active: !state.active
      });
    case 'SUBMIT':
      return Object.assign({}, state, {
        isSubmitting: true
      });
    case 'SUBMIT_SUCCESS':
      return Object.assign({}, state, {
        submitMessageType: 'SUCCESS',
        isSubmitting: false
      });
    case 'SUBMIT_ERROR':
      return Object.assign({}, state, {
        submitMessageType: 'ERROR',
        isSubmitting: false
      });
    case 'SUBMIT_SHIPPING_CODE':
      return Object.assign({}, state, {
        submitMessageType: 'SHIPPING_CODE',
        isSubmitting: false
      });
    case 'AUTO_DISCOUNT_PROMO':
      return Object.assign({}, state, {
        autoDiscountPromo: true
      });
    case 'SUBMIT_FROM_FORM':
      return Object.assign({}, state, {
        input: action.input,
        active: true,
        isSubmitting: true
      });
    case 'RESET_STATE':
      return {
        input: '',
        submitMessageType: '',
        active: false,
        isSubmitting: false,
        autoDiscountPromo: false
      };
    default:
      return state;
  }
};

var _ref = React.createElement(LoadingSpinner, null);

var _ref2 = React.createElement(BtnArrow, { color: '#FFFFFF' });

var CartDiscount = function CartDiscount(props) {
  var _useReducer = useReducer(cartDiscountReducer, {
    input: '',
    submitMessageType: '',
    active: false,
    isSubmitting: false,
    autoDiscountPromo: false
  }),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var addDiscountCode = props.addDiscountCode,
      autoDiscountCode = props.autoDiscountCode,
      autoDiscountPromo = props.autoDiscountPromo;

  useEffect(function () {
    var urlParamDiscount = queryString.parse(window.location.search).discount_code;
    // if auto discount => set auto discount code on load
    if (autoDiscountPromo && autoDiscountCode) {
      dispatch({ type: 'INPUT_CHANGE', input: autoDiscountCode });
      addDiscountCode(autoDiscountCode, handleSubmitCallback);
      dispatch({ type: 'AUTO_DISCOUNT_PROMO' });
      // else if urlParamDiscount => set url param code on load
    } else if (urlParamDiscount) {
      var paramCode = urlParamDiscount.toUpperCase();
      dispatch({ type: 'INPUT_CHANGE', input: paramCode });
      addDiscountCode(paramCode, handleSubmitCallback);
      // else if local storage => set local storage code on load
    } else if (localStorage.getItem('dormify_checkout_discount_code')) {
      var code = localStorage.getItem('dormify_checkout_discount_code');
      dispatch({ type: 'INPUT_CHANGE', input: code });
      addDiscountCode(code, handleSubmitCallback);
    }
  }, [handleSubmitCallback, addDiscountCode, autoDiscountCode, autoDiscountPromo]);

  useEffect(function () {
    if (props.discountCode) {
      if (props.discountCode.node.targetType === 'SHIPPING_LINE') {
        dispatch({ type: 'SUBMIT_SHIPPING_CODE' });
      }
    } else if (state.input && props.discountCode === undefined) {
      dispatch({ type: 'SUBMIT_ERROR' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.discountCode]);

  var formSubmitted = props.formSubmitted,
      formDiscountCode = props.formDiscountCode;

  useEffect(function () {
    if (formSubmitted === true && formDiscountCode) {
      dispatch({ type: 'SUBMIT_FROM_FORM', input: formDiscountCode });
      addDiscountCode(formDiscountCode, handleSubmitCallback);
    }
  }, [handleSubmitCallback, formSubmitted, formDiscountCode, addDiscountCode]);

  var removeDiscountCode = props.removeDiscountCode;

  var removeDiscountCodeData = useCallback(function () {
    localStorage.removeItem('dormify_checkout_discount_code');
    removeDiscountCode();
  }, [removeDiscountCode]);

  var handleCheckboxChange = function handleCheckboxChange() {
    if (state.active === true && state.input != '') {
      dispatch({ type: 'RESET_STATE' });
      removeDiscountCodeData();
    } else {
      dispatch({ type: 'TOGGLE_ACTIVE' });
    }
  };

  var handleInputChange = function handleInputChange(event) {
    dispatch({ type: 'INPUT_CHANGE', input: event.target.value.toUpperCase() });
  };

  var handleSubmitCallback = useCallback(function (userErrors) {
    if (userErrors.length) {
      dispatch({ type: 'SUBMIT_ERROR' });
      removeDiscountCodeData();
    } else {
      dispatch({ type: 'SUBMIT_SUCCESS' });
    }
  }, [dispatch, removeDiscountCodeData]);

  var detectEnterKeyDown = function detectEnterKeyDown(e) {
    if (e.keyCode === 13) {
      dispatch({ type: 'SUBMIT' });
      props.addDiscountCode(state.input, handleSubmitCallback);
    }
  };

  var handleSubmit = function handleSubmit() {
    dispatch({ type: 'SUBMIT' });
    props.addDiscountCode(state.input, handleSubmitCallback);
  };

  return React.createElement(
    CartCheckbox,
    {
      text: 'Add Discount Code',
      checked: state.active,
      onChange: handleCheckboxChange,
      maxHeight: '100px'
    },
    React.createElement(
      StyledForm,
      { error: state.submitMessageType === 'ERROR' },
      React.createElement('input', {
        name: 'discount',
        className: 'input-full',
        placeholder: 'Enter Discount...',
        'aria-label': 'Discount code input',
        value: state.input,
        onChange: handleInputChange,
        onKeyUp: detectEnterKeyDown
      }),
      React.createElement(
        'button',
        {
          className: 'btn',
          'aria-label': 'Submit Discount Code',
          onClick: handleSubmit
        },
        state.isSubmitting ? _ref : _ref2
      )
    ),
    state.submitMessageType && React.createElement(CartDiscountMessage, {
      code: state.input,
      messageType: state.submitMessageType,
      autoDiscountPromo: state.autoDiscountPromo,
      autoDiscountCodeEndDate: props.autoDiscountCodeEndDate
    })
  );
};

CartDiscount.propTypes = {
  autoDiscountPromo: PropTypes.bool,
  autoDiscountCode: PropTypes.string,
  discountCode: PropTypes.object,
  addDiscountCode: PropTypes.func.isRequired,
  removeDiscountCode: PropTypes.func.isRequired,
  autoDiscountCodeEndDate: PropTypes.string,
  formSubmitted: PropTypes.bool,
  formDiscountCode: PropTypes.string
};

export default CartDiscount;